import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, CreditCard, Ban, Shield, Globe } from 'lucide-react';
import SEO from '../components/SEO';

const TermsConditionsPage = () => {
  const lastUpdated = "January 26, 2025";
  const effectiveDate = "January 26, 2025";

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Terms & Conditions - MoveMate"
        description="MoveMate Terms and Conditions - Read our terms of service for using the MoveMate platform."
        url="https://movemate.info/terms"
      />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-cyan-500 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <FileText size={40} />
            <h1 className="text-3xl font-bold">Terms & Conditions</h1>
          </div>
          <p className="text-cyan-100">Last Updated: {lastUpdated} | Effective: {effectiveDate}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and MoveMate ("Company," "we," "us," or "our") governing your access to and use of the MoveMate platform, including our website, mobile applications, and related services (collectively, the "Service").
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              By accessing or using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must not access or use the Service.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-4">
              <p className="text-yellow-800 flex items-center gap-2">
                <AlertTriangle size={20} />
                <strong>Important:</strong> Please read these Terms carefully before using our Service.
              </p>
            </div>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li><strong>"Account"</strong> means the account you create to access the Service.</li>
              <li><strong>"Company Account"</strong> means an account created by a moving company to manage their business.</li>
              <li><strong>"Customer"</strong> means an individual or entity using the Service to arrange moving services.</li>
              <li><strong>"Moving Company"</strong> means a business that provides moving and relocation services.</li>
              <li><strong>"Content"</strong> means any information, data, text, photos, or other materials uploaded to or transmitted through the Service.</li>
              <li><strong>"Subscription"</strong> means the paid plan that provides access to premium features.</li>
            </ul>
          </section>

          {/* User Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Scale size={24} className="text-primary" />
              3. Eligibility and Account Registration
            </h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.1 Eligibility</h3>
            <p className="text-gray-600 leading-relaxed">
              You must be at least 18 years old and have the legal capacity to enter into binding agreements to use our Service. By using the Service, you represent and warrant that you meet these requirements.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.2 Account Creation</h3>
            <p className="text-gray-600 leading-relaxed">
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-2">
              <li>Provide accurate, current, and complete information during registration.</li>
              <li>Maintain and update your information to keep it accurate and current.</li>
              <li>Keep your password secure and confidential.</li>
              <li>Notify us immediately of any unauthorized access to your account.</li>
              <li>Accept responsibility for all activities that occur under your account.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">3.3 Company Accounts</h3>
            <p className="text-gray-600 leading-relaxed">
              If you create a Company Account, you represent and warrant that you have the authority to bind the company to these Terms and that the company is legally registered to operate moving services in its jurisdiction.
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Service Description</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              MoveMate provides a software platform that helps moving companies manage their operations, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Job management and scheduling</li>
              <li>Customer relationship management</li>
              <li>Inventory tracking with QR codes</li>
              <li>Estimate and invoice generation</li>
              <li>Real-time driver tracking</li>
              <li>Team communication tools</li>
              <li>Mobile applications for field staff</li>
              <li>Integration with third-party services</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              <strong>Important:</strong> MoveMate is a software platform only. We do not provide moving services ourselves. All moving services are provided by independent moving companies using our platform.
            </p>
          </section>

          {/* Subscriptions and Payment */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard size={24} className="text-primary" />
              5. Subscriptions and Payment
            </h2>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.1 Free Trial</h3>
            <p className="text-gray-600 leading-relaxed">
              New Company Accounts may be eligible for a free trial period. During the trial, you will have access to premium features. At the end of the trial, you must subscribe to continue using premium features.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.2 Subscription Plans</h3>
            <p className="text-gray-600 leading-relaxed">
              Subscription fees are based on the plan selected and are billed in advance on a monthly or annual basis. Prices are subject to change with 30 days' notice.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.3 Payment</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Payment is due at the beginning of each billing cycle.</li>
              <li>We accept major credit cards and other payment methods as displayed.</li>
              <li>All fees are non-refundable except as required by law or as stated in our refund policy.</li>
              <li>You are responsible for all applicable taxes.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.4 Cancellation</h3>
            <p className="text-gray-600 leading-relaxed">
              You may cancel your subscription at any time. Cancellation will take effect at the end of the current billing period. No refunds will be provided for partial months.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">5.5 AI Credits</h3>
            <p className="text-gray-600 leading-relaxed">
              Certain AI features (such as inventory scanning) consume AI credits. Credits are included in your subscription plan and additional credits may be purchased separately.
            </p>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Responsibilities and Conduct</h2>
            <p className="text-gray-600 leading-relaxed mb-4">You agree to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Use the Service only for lawful purposes and in compliance with these Terms.</li>
              <li>Comply with all applicable laws, regulations, and industry standards.</li>
              <li>Obtain all necessary consents before uploading customer data or personal information.</li>
              <li>Not use the Service to transmit harmful, offensive, or illegal content.</li>
              <li>Not attempt to gain unauthorized access to any part of the Service.</li>
              <li>Not interfere with or disrupt the Service or servers.</li>
              <li>Not reverse engineer, decompile, or disassemble any part of the Service.</li>
              <li>Not use automated systems to access the Service without permission.</li>
              <li>Not resell or redistribute the Service without authorization.</li>
            </ul>
          </section>

          {/* Prohibited Uses */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Ban size={24} className="text-red-500" />
              7. Prohibited Uses
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">You may not use the Service to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Violate any applicable law or regulation.</li>
              <li>Infringe on intellectual property rights of others.</li>
              <li>Transmit viruses, malware, or other harmful code.</li>
              <li>Harass, abuse, or harm another person.</li>
              <li>Send spam or unsolicited communications.</li>
              <li>Engage in fraudulent activities.</li>
              <li>Collect user information without consent.</li>
              <li>Impersonate another person or entity.</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">8.1 Our Intellectual Property</h3>
            <p className="text-gray-600 leading-relaxed">
              The Service and its original content, features, and functionality are owned by MoveMate and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">8.2 Your Content</h3>
            <p className="text-gray-600 leading-relaxed">
              You retain ownership of any Content you submit to the Service. By submitting Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display the Content as necessary to provide the Service.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">8.3 Feedback</h3>
            <p className="text-gray-600 leading-relaxed">
              Any feedback, suggestions, or ideas you provide about the Service may be used by us without any obligation to you.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimers</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY DEFECTS WILL BE CORRECTED.
              </p>
              <p className="text-gray-600 leading-relaxed mt-4">
                MOVEMATE IS NOT RESPONSIBLE FOR THE QUALITY, TIMELINESS, OR LEGALITY OF MOVING SERVICES PROVIDED BY MOVING COMPANIES USING OUR PLATFORM.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL MOVEMATE, ITS DIRECTORS, EMPLOYEES, PARTNERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-4">
                <li>Your access to or use of or inability to access or use the Service;</li>
                <li>Any conduct or content of any third party on the Service;</li>
                <li>Any content obtained from the Service;</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mt-4">
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree to defend, indemnify, and hold harmless MoveMate and its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or expenses arising from:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4 mt-4">
              <li>Your use of the Service;</li>
              <li>Your violation of these Terms;</li>
              <li>Your violation of any third-party rights;</li>
              <li>Any Content you submit to the Service.</li>
            </ul>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Upon termination, your right to use the Service will cease immediately. Provisions of these Terms that by their nature should survive termination shall survive.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe size={24} className="text-primary" />
              13. Governing Law and Jurisdiction
            </h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              For users in the European Union, you may also be entitled to bring proceedings in your local courts.
            </p>
          </section>

          {/* GDPR Compliance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield size={24} className="text-primary" />
              14. Data Protection and GDPR
            </h2>
            <p className="text-gray-600 leading-relaxed">
              We are committed to protecting your personal data in accordance with the General Data Protection Regulation (GDPR) and other applicable data protection laws. For details on how we collect, use, and protect your data, please see our{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              If you are a Moving Company using our Service, you are the data controller for customer personal data processed through the Service. You are responsible for ensuring you have appropriate legal bases and consents for processing.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Changes to These Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms on the Service and updating the "Last Updated" date.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Your continued use of the Service after changes become effective constitutes your acceptance of the revised Terms.
            </p>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Severability</h2>
            <p className="text-gray-600 leading-relaxed">
              If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will continue in full force and effect.
            </p>
          </section>

          {/* Entire Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Entire Agreement</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and MoveMate regarding the Service and supersede all prior agreements.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Contact Information</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700"><strong>MoveMate</strong></p>
              <p className="text-gray-600">Email: <a href="mailto:legal@movemate.info" className="text-primary hover:underline">legal@movemate.info</a></p>
              <p className="text-gray-600">Website: <a href="https://movemate.info" className="text-primary hover:underline">https://movemate.info</a></p>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            <Link to="/contact" className="text-primary hover:underline">Contact Us</Link>
            <Link to="/" className="text-primary hover:underline">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsConditionsPage;
